import { StoreActions } from '../../../core/context/store';
import { PaginatedResult } from '../../../core/lib/api';
import { recordToQueryString } from '../../../core/lib/util';
import { ApiService } from '../../../core/services';
import { AnatomicalModel } from '../models';
import { AppConfig } from '../../../core/config';
import { Profile } from '../../../core/context/auth/state';
import { AppEnvironments } from '../../../core/constants';

type GroupAllowList = {
  [key: string]: {
    [key: string]: number[];
  };
};

const modelAllowlistGroupBased: GroupAllowList = {
  production: {
    // Groups
    //  1: Corsmed test group
    'Dog UCD': [1],
    'Horse fetlock': [1],
    Pineapple: [1],
    Watermelon: [1],
    'Head (Fraunhofer)': [1, 189],
    'Phantom (Caliber MRI)': [1, 224],
  },
};

type GeneralAllowlist = {
  [key: string]: string[];
};

const modelAllowlistGeneral: GeneralAllowlist = {
  production: [
    'Brain (segmented tissues, optional pathologies)',
    'Cervical Spine',
    'Cervical/thoracic spine',
    'Elbow',
    'Female Hips + Lumbar',
    'Female Pelvis',
    'Foot and ankle',
    'Foot and ankle (with implants)',
    'Hand and wrist',
    'Head (MRA)',
    'Head (segmented tissues)',
    'Knee',
    'Lumbar Spine',
    'Male body (segmented tissues, optional MI pathology)',
    'Male body (segmented tissues)',
    'Shoulder',
    'Thoracic Spine',
    'Whole spine',
  ],
};

export function isModelEnabled(profile: Profile, model: AnatomicalModel): boolean {
  // integration and development environments always have all models enabled
  if (AppConfig.env === AppEnvironments.INTEGRATION || AppConfig.env === AppEnvironments.DEVELOPMENT) {
    return true;
  }

  // check if enabled generally for all users
  const generalRules = modelAllowlistGeneral[AppConfig.env];
  if (generalRules) {
    const isEnabled = generalRules.some((name) => model.name === name);
    if (isEnabled) {
      return true;
    }
  }

  // Check if enabled for the current user based on group rules
  const groupBasedRules = modelAllowlistGroupBased[AppConfig.env];
  if (groupBasedRules && groupBasedRules[model.name]) {
    const allowedGroups = groupBasedRules[model.name];
    const userGroups = (profile?.groups || []).map((g) => g.id);

    const isEnabled = userGroups.some((ug) => allowedGroups.some((wg) => wg === ug));
    if (isEnabled) {
      return true;
    }
  }

  return false;
}

export const load =
  (page: number, perPage?: number) =>
  async (actions: StoreActions<AnatomicalModel[]>): Promise<void> => {
    try {
      const params = {
        page,
        per_page: perPage || 50,
        order_by: 'id',
        order_dir: 'asc',
      };

      actions.startLoading(params);
      const queryParams = recordToQueryString(params);
      const response = await ApiService.get<PaginatedResult<AnatomicalModel>>(`v1/anatomical-models/?${queryParams}`);
      const { results, paginator } = response.data;
      actions.setValue(results, paginator as unknown as Record<string, unknown>);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      actions.setError(err.message);
    }
  };

// /:UserID/single/:AnatomicalModelID
export const getById =
  (userId: number, id: number) =>
  async (actions: StoreActions<AnatomicalModel>): Promise<void> => {
    try {
      actions.startLoading();
      const response = await ApiService.get<AnatomicalModel>(`v1/anatomical-models/${userId}/single/${id}`);
      actions.setValue(response.data);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      actions.setError(err.message);
    }
  };

export default load;
